// ══════════════════════════════════════════════════════
// Strategy Lab — SVG Chart Components
// ══════════════════════════════════════════════════════

import { useState, useRef } from "react";
import { theme, fonts } from "../../theme";
import { formatPnl } from "../../components/ui";

/* ── Equity Curve ─────────────────────────────── */
export function EquityChart({ data, W=700, H=200 }) {
  const [tip,setTip] = useState(null);
  const ref = useRef(null);
  if (!data || data.length < 2) return <div style={{ padding:24, textAlign:"center", color:theme.text4, fontSize:12 }}>No equity data</div>;
  const v = data.map(d => d.equity), mn = Math.min(...v), mx = Math.max(...v), rng = mx-mn || 1;
  const pT=16, pB=28, pL=60, pR=16, cW=W-pL-pR, cH=H-pT-pB;
  const pts = data.map((d,i) => `${pL+(i/(data.length-1))*cW},${pT+cH-((d.equity-mn)/rng)*cH}`);
  const up = v[v.length-1] >= v[0];
  const sc = up ? theme.green : theme.red;
  const fc = up ? "rgba(22,163,74,0.06)" : "rgba(220,38,38,0.04)";
  const area = `M${pts[0]} ${pts.slice(1).map(p => `L${p}`).join(" ")} L${pL+cW},${pT+cH} L${pL},${pT+cH} Z`;
  const tks = Array.from({ length:5 }, (_,i) => mn + (rng*i)/4);
  const onMove = e => {
    if (!ref.current) return;
    const rect = ref.current.getBoundingClientRect();
    const sx = (e.clientX - rect.left) / rect.width * W;
    const idx = Math.round(((sx - pL) / cW) * (data.length - 1));
    if (idx >= 0 && idx < data.length) {
      const d = data[idx];
      setTip({ x:e.clientX-rect.left, y:e.clientY-rect.top, date:d.timestamp?.slice(0,10), equity:d.equity, pnl:d.pnl });
    }
  };
  return <div ref={ref} style={{ position:"relative" }} onMouseMove={onMove} onMouseLeave={() => setTip(null)}>
    {tip && <div style={{ position:"absolute", left:tip.x+12, top:tip.y-40, background:theme.text, color:"#fff", padding:"6px 10px", borderRadius:6, fontSize:10, fontFamily:fonts.mono, pointerEvents:"none", zIndex:10, whiteSpace:"nowrap", boxShadow:"0 4px 12px rgba(0,0,0,0.15)" }}>
      {tip.date} · ${tip.equity?.toLocaleString()} {tip.pnl != null && <span style={{ color:tip.pnl >= 0 ? "#6ee7b7" : "#fca5a5" }}> ({tip.pnl >= 0 ? "+" : ""}{tip.pnl?.toFixed(0)})</span>}
    </div>}
    <svg viewBox={`0 0 ${W} ${H}`} style={{ width:"100%", height:"auto" }}>
      {tks.map((t,i) => { const y = pT + cH - ((t-mn)/rng)*cH; return <g key={i}><line x1={pL} y1={y} x2={pL+cW} y2={y} stroke={theme.divider} strokeWidth={1}/><text x={pL-8} y={y+3} textAnchor="end" fill={theme.text4} fontSize={9} fontFamily={fonts.mono}>${(t/1000).toFixed(0)}k</text></g> })}
      <path d={area} fill={fc}/>
      <polyline points={pts.join(" ")} fill="none" stroke={sc} strokeWidth={1.8} strokeLinejoin="round"/>
      <text x={pL} y={H-4} fill={theme.text4} fontSize={8} fontFamily={fonts.mono}>{data[0].timestamp?.slice(0,10)}</text>
      <text x={pL+cW} y={H-4} textAnchor="end" fill={theme.text4} fontSize={8} fontFamily={fonts.mono}>{data[data.length-1].timestamp?.slice(0,10)}</text>
    </svg>
  </div>;
}

/* ── Drawdown ─────────────────────────────────── */
export function DrawdownChart({ data, W=700, H=120 }) {
  if (!data || data.length < 2) return null;
  const v = data.map(d => d.drawdown_pct), mx = Math.max(...v), rng = mx || 1;
  const pT=8, pB=20, pL=50, pR=16, cW=W-pL-pR, cH=H-pT-pB;
  const pts = data.map((d,i) => `${pL+(i/(data.length-1))*cW},${pT+(d.drawdown_pct/rng)*cH}`);
  const area = `M${pL},${pT} ${pts.map(p => `L${p}`).join(" ")} L${pL+cW},${pT} Z`;
  return <svg viewBox={`0 0 ${W} ${H}`} style={{ width:"100%", height:"auto" }}>
    <line x1={pL} y1={pT} x2={pL+cW} y2={pT} stroke={theme.divider} strokeWidth={1}/>
    <text x={pL-6} y={pT+4} textAnchor="end" fill={theme.text4} fontSize={8} fontFamily={fonts.mono}>0%</text>
    <text x={pL-6} y={pT+cH+4} textAnchor="end" fill={theme.redText} fontSize={8} fontFamily={fonts.mono}>-{mx.toFixed(1)}%</text>
    <path d={area} fill="rgba(220,38,38,0.08)"/>
    <polyline points={pts.join(" ")} fill="none" stroke={theme.red} strokeWidth={1.5} strokeLinejoin="round"/>
  </svg>;
}

/* ── Monthly Heatmap ──────────────────────────── */
export function MonthlyHeatmap({ data }) {
  if (!data || Object.keys(data).length === 0) return <div style={{ padding:20, textAlign:"center", color:theme.text4, fontSize:12 }}>No monthly data</div>;
  const ent = Object.entries(data).sort(([a],[b]) => a.localeCompare(b));
  const yrs = [...new Set(ent.map(([k]) => k.slice(0,4)))];
  const mo = ["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"];
  return <div style={{ overflowX:"auto" }}>
    <table style={{ width:"100%", borderCollapse:"collapse", fontSize:11, fontFamily:fonts.mono }}>
      <thead><tr>
        <th style={{ padding:"5px 8px", textAlign:"left", fontSize:9, fontWeight:700, color:theme.text4 }}>Year</th>
        {mo.map(m => <th key={m} style={{ padding:"5px 5px", textAlign:"center", fontSize:9, fontWeight:700, color:theme.text4 }}>{m}</th>)}
        <th style={{ padding:"5px 8px", textAlign:"right", fontSize:9, fontWeight:700, color:theme.text4 }}>Total</th>
      </tr></thead>
      <tbody>{yrs.map(yr => {
        let tot = 0;
        return <tr key={yr}>
          <td style={{ padding:"4px 8px", fontWeight:700, color:theme.text2 }}>{yr}</td>
          {mo.map((_,mi) => {
            const k = `${yr}-${String(mi+1).padStart(2,"0")}`, v = data[k];
            if (v != null) tot += v;
            const u = v > 0, int = v != null ? Math.min(Math.abs(v)/2000, 1) : 0;
            const bg = v == null ? "transparent" : u ? `rgba(22,163,74,${0.06+int*0.18})` : `rgba(220,38,38,${0.05+int*0.15})`;
            return <td key={k} style={{ padding:"4px 4px", textAlign:"center", borderRadius:4, background:bg, color:v == null ? theme.text5 : u ? theme.greenText : theme.redText, fontWeight:600, fontSize:10 }}>
              {v != null ? `$${Math.round(v).toLocaleString()}` : "—"}
            </td>;
          })}
          <td style={{ padding:"4px 8px", textAlign:"right", fontWeight:800, color:tot >= 0 ? theme.greenText : theme.redText }}>${Math.round(tot).toLocaleString()}</td>
        </tr>;
      })}</tbody>
    </table>
  </div>;
}

/* ── Trial Convergence ────────────────────────── */
export function TrialChart({ trials, W=600, H=160 }) {
  const [tip,setTip] = useState(null);
  const ref = useRef(null);
  if (!trials || trials.length < 2) return null;
  const vals = trials.map(t => t.net_pnl || 0);
  const bests = [];
  let best = -Infinity;
  vals.forEach(v => { if (v > best) best = v; bests.push(best); });
  const all = [...vals, ...bests], mn = Math.min(...all), mx = Math.max(...all), rng = mx-mn || 1;
  const pT=12, pB=24, pL=55, pR=12, cW=W-pL-pR, cH=H-pT-pB;
  const xf = i => pL + (i/(trials.length-1)) * cW;
  const yf = v => pT + cH - ((v-mn)/rng) * cH;
  const onMove = e => {
    if (!ref.current) return;
    const rect = ref.current.getBoundingClientRect();
    const sx = (e.clientX-rect.left) / rect.width * W;
    const idx = Math.round(((sx-pL)/cW) * (trials.length-1));
    if (idx >= 0 && idx < trials.length) {
      const t = trials[idx];
      setTip({ x:e.clientX-rect.left, y:e.clientY-rect.top, trial:t.trial, pnl:t.net_pnl, score:t.score, grade:t.grade });
    }
  };
  return <div ref={ref} style={{ position:"relative" }} onMouseMove={onMove} onMouseLeave={() => setTip(null)}>
    {tip && <div style={{ position:"absolute", left:tip.x+12, top:tip.y-44, background:theme.text, color:"#fff", padding:"6px 10px", borderRadius:6, fontSize:10, fontFamily:fonts.mono, pointerEvents:"none", zIndex:10, whiteSpace:"nowrap", boxShadow:"0 4px 12px rgba(0,0,0,0.15)" }}>
      Trial {tip.trial} · P&L: {formatPnl(tip.pnl)} · Score: {((tip.score||0)*100).toFixed(0)} · {tip.grade}
    </div>}
    <svg viewBox={`0 0 ${W} ${H}`} style={{ width:"100%", height:"auto" }}>
      {[mn, mn+rng/2, mx].map((t,i) => <g key={i}><line x1={pL} y1={yf(t)} x2={pL+cW} y2={yf(t)} stroke={theme.divider} strokeWidth={1}/><text x={pL-6} y={yf(t)+3} textAnchor="end" fill={theme.text4} fontSize={8} fontFamily={fonts.mono}>{formatPnl(t)}</text></g>)}
      {vals.map((v,i) => <circle key={i} cx={xf(i)} cy={yf(v)} r={2.5} fill={v > 0 ? theme.green : theme.red} opacity={0.5}/>)}
      <polyline points={bests.map((b,i) => `${xf(i)},${yf(b)}`).join(" ")} fill="none" stroke={theme.blue} strokeWidth={2} strokeLinejoin="round"/>
      <text x={pL} y={H-4} fill={theme.text4} fontSize={8} fontFamily={fonts.mono}>Trial 1</text>
      <text x={pL+cW} y={H-4} textAnchor="end" fill={theme.text4} fontSize={8} fontFamily={fonts.mono}>Trial {trials.length}</text>
      <text x={pL+cW} y={pT-2} textAnchor="end" fill={theme.blueText} fontSize={9} fontWeight="700" fontFamily={fonts.mono}>Best P&L: {formatPnl(best)}</text>
    </svg>
  </div>;
}

/* ── Walk-Forward IS vs OOS Bars ──────────────── */
export function WFBars({ windows, W=600, H=160 }) {
  if (!windows || windows.length === 0) return null;
  const pT=12, pB=24, pL=50, pR=12, cW=W-pL-pR, cH=H-pT-pB;
  const all = windows.flatMap(w => [w.is_pnl||0, w.oos_pnl||0]);
  const mx = Math.max(...all.map(Math.abs)) || 1;
  const mid = pT + cH/2;
  const bW = Math.min(cW/(windows.length*3), 30);
  return <svg viewBox={`0 0 ${W} ${H}`} style={{ width:"100%", height:"auto" }}>
    <line x1={pL} y1={mid} x2={pL+cW} y2={mid} stroke={theme.border} strokeWidth={1}/>
    <text x={pL-6} y={mid+3} textAnchor="end" fill={theme.text4} fontSize={8} fontFamily={fonts.mono}>$0</text>
    {windows.map((w,i) => {
      const cx = pL + (i+0.5)/(windows.length) * cW;
      const isH = Math.abs(w.is_pnl||0)/mx * (cH/2);
      const oosH = Math.abs(w.oos_pnl||0)/mx * (cH/2);
      return <g key={i}>
        <rect x={cx-bW-1} y={w.is_pnl >= 0 ? mid-isH : mid} width={bW} height={isH} rx={2} fill="rgba(37,99,235,0.25)" stroke={theme.blue} strokeWidth={0.5}/>
        <rect x={cx+1} y={w.oos_pnl >= 0 ? mid-oosH : mid} width={bW} height={oosH} rx={2} fill={w.oos_pnl >= 0 ? "rgba(22,163,74,0.3)" : "rgba(220,38,38,0.25)"} stroke={w.oos_pnl >= 0 ? theme.green : theme.red} strokeWidth={0.5}/>
        <text x={cx} y={H-4} textAnchor="middle" fill={theme.text4} fontSize={7} fontFamily={fonts.mono}>W{w.window}</text>
      </g>;
    })}
    <rect x={pL+cW-80} y={pT} width={8} height={8} rx={2} fill="rgba(37,99,235,0.25)" stroke={theme.blue} strokeWidth={0.5}/>
    <text x={pL+cW-68} y={pT+7} fill={theme.text3} fontSize={8} fontFamily={fonts.mono}>IS</text>
    <rect x={pL+cW-44} y={pT} width={8} height={8} rx={2} fill="rgba(22,163,74,0.3)" stroke={theme.green} strokeWidth={0.5}/>
    <text x={pL+cW-32} y={pT+7} fill={theme.text3} fontSize={8} fontFamily={fonts.mono}>OOS</text>
  </svg>;
}

/* ── Pareto Scatter (Sprint C) ─────────────────── */
export function ParetoChart({ front, ax1Label="net_pnl", ax2Label="max_drawdown", onSelect, selected, W=680, H=380 }) {
  const [tip, setTip] = useState(null);
  if (!front || front.length === 0) return (
    <div style={{ padding:"32px 0", textAlign:"center", color:theme.text3, fontFamily:fonts.mono, fontSize:12 }}>
      Keine Pareto-Front verfügbar — run im Pareto-Modus starten
    </div>
  );

  const pT=24, pB=40, pL=64, pR=16;
  const cW=W-pL-pR, cH=H-pT-pB;

  const ax1Vals = front.map(d => d.ax1_val);
  const ax2Vals = front.map(d => d.ax2_val);
  const ax1Min = Math.min(...ax1Vals), ax1Max = Math.max(...ax1Vals);
  const ax2Min = Math.min(...ax2Vals), ax2Max = Math.max(...ax2Vals);
  const ax1Rng = ax1Max - ax1Min || 1;
  const ax2Rng = ax2Max - ax2Min || 1;

  // ax1 = x-axis (return, higher = right), ax2 = y-axis (drawdown, lower = better = top)
  const isDD2 = ax2Label.includes("drawdown") || ax2Label.includes("dd");
  const toX = v => pL + ((v - ax1Min) / ax1Rng) * cW;
  const toY = v => isDD2
    ? pT + ((v - ax2Min) / ax2Rng) * cH   // drawdown: lower = better = top
    : pT + cH - ((v - ax2Min) / ax2Rng) * cH;

  // Grid lines
  const niceVals = (min, max, n=5) => {
    const step = niceStep(max - min || 1, n);
    const start = Math.ceil(min / step) * step;
    const out = [];
    for (let v = start; v <= max + step * 0.01; v = Math.round((v + step) * 1e8) / 1e8) out.push(v);
    return out;
  };
  function niceStep(r, n=5) { const raw=r/n,e=Math.pow(10,Math.floor(Math.log10(raw||1))); return ([1,2,2.5,5,10].find(x=>x*e>=raw)||10)*e; }

  const fmtAxis = (v, label) => {
    if (label.includes("pnl") || label.includes("usd")) return `$${Math.round(v)}`;
    if (label.includes("pct") || label.includes("win_rate")) return `${v.toFixed(1)}%`;
    return v.toFixed(2);
  };

  const AXIS_LABELS = {
    net_pnl: "Net P&L ($)", max_drawdown_usd: "Max DD ($)",
    max_drawdown_pct: "Max DD (%)", win_rate: "Win Rate (%)",
    sharpe: "Sharpe Ratio", profit_factor: "Profit Factor",
    bucket_robust_score: "Score",
  };

  return (
    <div style={{ position:"relative", maxWidth:900, margin:"0 auto" }}>
      {tip && (
        <div style={{ position:"absolute", left:tip.px+14, top:tip.py-24, zIndex:20,
                      background:theme.text, color:"#fff", borderRadius:8, padding:"8px 12px",
                      fontSize:11, fontFamily:fonts.mono, pointerEvents:"none",
                      boxShadow:"0 4px 16px rgba(0,0,0,0.2)", whiteSpace:"nowrap" }}>
          <div style={{ fontWeight:700 }}>Trial #{tip.d.trial} · Grade {tip.d.grade}</div>
          <div>{AXIS_LABELS[ax1Label]||ax1Label}: <b>{fmtAxis(tip.d.ax1_val, ax1Label)}</b></div>
          <div>{AXIS_LABELS[ax2Label]||ax2Label}: <b>{fmtAxis(tip.d.ax2_val, ax2Label)}</b></div>
          <div style={{ opacity:0.7 }}>P&L {formatPnl(tip.d.net_pnl)} · DD {tip.d.max_drawdown?.toFixed(1)}% · WR {tip.d.win_rate?.toFixed(1)}%</div>
        </div>
      )}
      <svg viewBox={`0 0 ${W} ${H}`} style={{ width:"100%", height:"auto", maxHeight:500, overflow:"visible" }}>
        {/* Grid */}
        {niceVals(ax1Min, ax1Max).map((v,i) => (
          <g key={`gx${i}`}>
            <line x1={toX(v)} y1={pT} x2={toX(v)} y2={pT+cH} stroke={theme.divider} strokeWidth={1}/>
            <text x={toX(v)} y={pT+cH+14} textAnchor="middle" fill={theme.text4} fontSize={9} fontFamily={fonts.mono}>{fmtAxis(v,ax1Label)}</text>
          </g>
        ))}
        {niceVals(ax2Min, ax2Max).map((v,i) => (
          <g key={`gy${i}`}>
            <line x1={pL} y1={toY(v)} x2={pL+cW} y2={toY(v)} stroke={theme.divider} strokeWidth={1}/>
            <text x={pL-6} y={toY(v)+3} textAnchor="end" fill={theme.text4} fontSize={9} fontFamily={fonts.mono}>{fmtAxis(v,ax2Label)}</text>
          </g>
        ))}

        {/* Chart border */}
        <rect x={pL} y={pT} width={cW} height={cH} fill="none" stroke={theme.border} strokeWidth={1}/>

        {/* Pareto front line */}
        {front.length > 1 && (
          <polyline
            points={[...front].sort((a,b)=>a.ax1_val-b.ax1_val).map(d=>`${toX(d.ax1_val)},${toY(d.ax2_val)}`).join(" ")}
            fill="none" stroke={theme.blue} strokeWidth={1.5} strokeDasharray="6,3" opacity={0.5}
          />
        )}

        {/* Points */}
        {front.map((d, i) => {
          const cx = toX(d.ax1_val), cy = toY(d.ax2_val);
          const isSel = selected?.trial === d.trial;
          const gradeColor = d.grade==="A" ? theme.green : d.grade==="B" ? theme.blue : theme.amber || "#f59e0b";
          return (
            <g key={i} style={{ cursor:"pointer" }}
              onClick={() => onSelect?.(d)}
              onMouseEnter={e => { const r=e.currentTarget.closest("svg").getBoundingClientRect(); setTip({ d, px:cx, py:cy }); }}
              onMouseLeave={() => setTip(null)}>
              <circle cx={cx} cy={cy} r={isSel ? 9 : 6.5}
                fill={gradeColor} fillOpacity={isSel ? 1 : 0.7}
                stroke={isSel ? "#fff" : gradeColor} strokeWidth={isSel ? 2.5 : 1}/>
              {isSel && <text x={cx} y={cy+3} textAnchor="middle" fill="#fff" fontSize={7} fontFamily={fonts.mono} fontWeight="700">{d.trial}</text>}
            </g>
          );
        })}

        {/* Axis labels */}
        <text x={pL+cW/2} y={H-2} textAnchor="middle" fill={theme.text3} fontSize={10} fontFamily={fonts.mono} fontWeight="600">
          {AXIS_LABELS[ax1Label]||ax1Label} →
        </text>
        <text x={12} y={pT+cH/2} textAnchor="middle" fill={theme.text3} fontSize={10} fontFamily={fonts.mono} fontWeight="600"
          transform={`rotate(-90, 12, ${pT+cH/2})`}>
          {AXIS_LABELS[ax2Label]||ax2Label} {isDD2?"↓ (lower=better)":"→"}
        </text>

        {/* Legend — fixed spacing + background box for readability */}
        <rect x={pL+cW-200} y={pT-2} width={195} height={20} fill="#fff" stroke={theme.border} strokeWidth={0.5} rx={3} opacity={0.95}/>
        {[["A",theme.green],["B",theme.blue],["C",theme.amber||"#f59e0b"]].map(([g,c],i) => (
          <g key={g}>
            <circle cx={pL+cW-190+i*65} cy={pT+8} r={5} fill={c} fillOpacity={0.7}/>
            <text x={pL+cW-181+i*65} y={pT+11} fill={theme.text2} fontSize={9} fontFamily={fonts.mono} fontWeight={600}>Grade {g}</text>
          </g>
        ))}
      </svg>
      <div style={{ textAlign:"right", fontSize:10, color:theme.text3, fontFamily:fonts.mono, marginTop:4 }}>
        {front.length} nicht-dominierte Trials auf der Pareto-Front · Klick = Parameter anzeigen
      </div>
    </div>
  );
}
